import { App, Button, Flex, Input, Select, Tooltip } from "antd";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import api, { supplierPriceHis } from "@/apis/supplier";
import { CommonListParams, CommonStateEnum } from "@/types/common";
import useDataUpdate from "@/hooks/useDataUpdate";
import useDataAdd from "@/hooks/useDataAdd";
import useDataDel from "@/hooks/useDataDel";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import useSupplierColumn from "../hooks/useSupplierColumn";
import SupplierForm from "./SupplierForm";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { CustomTabPaneContext } from "@/components/TabPane";
import { Supplier, SupplierAdd, SupplierPrice } from "@/types/purchase";
import { DictContext } from "@/layout/context/DictContext";
import { generatePriceTable, replaceCurrencyKeys } from "../pages/Price/utils";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface SupplierFilters {
  keyword: string;
  state: CommonStateEnum;
}

const INITIAL_SUPPLIER_FILTERS: SupplierFilters = {
  keyword: "",
  state: CommonStateEnum.ACTIVE,
};

const CustomerTable: FC = () => {
  const { t } = useTranslation();

  const { currencys, purchasePayMethod, orgs, purchaseTradeTerm } = useContext(DictContext);

  const { onOpen } = useContext(CustomTabPaneContext);
  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [list, total, listLoading, { fetchList, addItem, rmItem, updateItem }] = useDataList<Supplier, [CommonListParams, CommonStateEnum]>({ fetchApi: api.list });
  const { update, loading: updateLoading } = useDataUpdate<[SupplierAdd, string]>({ api: api.update });
  const { add, loading: addLoading } = useDataAdd<[SupplierAdd], Supplier>({ api: api.add });
  const { del } = useDataDel({ api: api.disable });
  const { del: enable } = useDataDel({ api: api.enable });

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<SupplierFilters>(INITIAL_SUPPLIER_FILTERS);
  const { keyword, state } = draftFilters;
  const appliedState = appliedFilters.state;
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [detail, setDetail] = useState<Supplier | null>(null);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState<number>(0);
  const [exportProgressVisible, setExportProgressVisible] = useState(false);
  const [exportPriceProgress, setExportPriceProgress] = useState(0);
  const { onConfirm } = useFrontDelTip({ message: t("layout.confirmDisableSupplier"), desc: t("layout.confirmDisableSupplierDesc"), cb: (id: string) => onDel(id) });
  const { onConfirm: onEnable } = useFrontDelTip({ message: t("layout.confirmEnableSupplier"), desc: t("layout.confirmEnableSupplierDesc"), cb: (id: string) => doEnable(id) });
  // 导出全部数据加载函数
  const onExportPrice = async (supplier?: Supplier, isAll?: boolean) => {
    setExportProgressVisible(true);
    setExportPriceProgress(0);
    let allData: SupplierPrice[] = [];
    let offset = 0;
    let take = 100;
    let hasMore = true;

    try {
      while (hasMore) {
        const response = await supplierPriceHis(supplier?.id ?? '', !!isAll, {
          pagination: { offset, take },
        });
        if (response.body && response.body.length > 0) {
          allData.push(...response.body);
          offset += take;
          const progress = Math.min(
            Math.round(
              (allData.length /
                (response.pagination?.size || allData.length)) *
              100
            ),
            99
          );
          setExportPriceProgress(progress);

          if (response.body.length < take || !!!isAll) {  //单个无需分页
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      let resData = replaceCurrencyKeys(allData, currencys, '2');
      let fileName = isAll ? `${t('purchase.allPriceHis')}.xlsx` : `${supplier?.code ?? ""}-${t('purchase.supplierHisPrice')}.xlsx`
      generatePriceTable(t, isAll ? resData : resData.map(data => ({ ...data, supplier: supplier! })), fileName);
    } finally {
      setExportProgressVisible(false);
      setExportPriceProgress(0);
    }
  };

  const columns = useSupplierColumn({
    onDel: onConfirm,
    onEnable: onEnable,
    onDetail: setDetail,
    onOpenTab: onOpen,
    onExportPrice,
    state: appliedState,
  });

  useEffect(() => {
    fetch(0, INITIAL_SUPPLIER_FILTERS);
    // 首次进入页面时初始化列表，不能跟随每次 render 生成的 fetch 重复请求。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errors: {
    [key in number]: string;
  } = {
    10010: t("common.networkError"),
    71601: t("purchase.supplierNotExisted"),
    71602: t("purchase.codeExisted"),
  };

  const onDel = (id: string) => {
    return del(id)
      .then(() => {
        rmItem(id);
        notification.success({ placement: 'top', message: t('product.disableSuccess') })
      })
      .catch(() => {
        notification.error({ placement: 'top', message: t('product.disableFailed') })
      });
  };

  const doEnable = (id: string) => {
    return enable(id)
      .then(() => {
        rmItem(id);
        notification.success({ placement: 'top', message: t('product.enableSuccess') })
      })
      .catch(() => {
        notification.error({ placement: 'top', message: t('product.enableFailed') })
      });
  };

  const fetch = (offset: number, filters: SupplierFilters) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE
    fetchList({
      body: filters.keyword,
      pagination: { offset, take },
    }, filters.state).catch((err) => {
      switch (err) {
        case 10010:
          break;
        default:
          break;
      }
    });
  };

  const handleExportAll = async (): Promise<Supplier[]> => {
    setExportLoading(true);
    setExportProgress(0);

    try {
      const res = await api.fetchs({
        body: appliedFilters.keyword,
      });
      const suppliers = res?.body ?? [];
      setExportProgress(100);
      return suppliers;
    } catch (error) {
      notification.error({ placement: 'top', message: t('common.exportErr') });
      throw error;
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_SUPPLIER_FILTERS);
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  // 导出列配置
  const exportColumns = (t: any) => [
    { key: "purchase.code", label: t("purchase.code") },
    { key: "purchase.principal", label: t("purchase.principal") },
    { key: "purchase.name", label: t("purchase.name") },
    { key: "purchase.shortName", label: t("purchase.shortName") },
    { key: "purchase.country", label: t("purchase.country") },
    { key: "purchase.address", label: t("purchase.address") },
    { key: "purchase.zipCode", label: t("purchase.zipCode") },
    { key: "purchase.email", label: t("purchase.email") },
    { key: "purchase.contact", label: t("purchase.contact") },
    { key: "purchase.phone", label: t("purchase.phone") },
    { key: "purchase.fax", label: t("purchase.fax") },
    { key: "purchase.mobile", label: t("purchase.mobile") },
    { key: "purchase.account1", label: t("purchase.account1") },
    { key: "purchase.bank1", label: t("purchase.bank1") },
    { key: "purchase.swiftCode1", label: t("purchase.swiftCode1") },
    { key: "purchase.account2", label: t("purchase.account2") },
    { key: "purchase.bank2", label: t("purchase.bank2") },
    { key: "purchase.swiftCode2", label: t("purchase.swiftCode2") },
    { key: "purchase.currency", label: t("purchase.currency") },
    { key: "purchase.creditLevel", label: t("purchase.creditLevel") },
    { key: "purchase.creditLine", label: t("purchase.creditLine") },
    { key: "purchase.depositRate", label: t("purchase.depositRate") },
    { key: "purchase.tradeTerm", label: t("purchase.tradeTerm") },
    { key: "purchase.payMethod", label: t("purchase.payMethod") },
    { key: "purchase.affiliates", label: t("purchase.affiliates") },
    { key: "purchase.org", label: t("purchase.org") },
    { key: "purchase.billingDays", label: t("purchase.billingDays") },
    { key: "purchase.taxRate", label: t("purchase.taxRate") },
    { key: "purchase.thdCode", label: t("purchase.thdCode") },
  ];

  // 默认选中的列
  const defaultSelectedColumns = (t: any) => [
    'purchase.code',
    'purchase.principal',
    'purchase.name',
    'purchase.shortName',
    'purchase.country',
    'purchase.address',
    'purchase.zipCode',
    'purchase.email',
    'purchase.contact',
    'purchase.phone',
    'purchase.fax',
    'purchase.mobile',
    'purchase.account1',
    'purchase.bank1',
    'purchase.swiftCode1',
    'purchase.account2',
    'purchase.bank2',
    'purchase.swiftCode2',
    'purchase.currency',
    'purchase.creditLevel',
    'purchase.creditLine',
    'purchase.depositRate',
    'purchase.tradeTerm',
    'purchase.payMethod',
    'purchase.affiliates',
    'purchase.org',
    'purchase.billingDays',
    'purchase.taxRate',
    'purchase.thdCode',
  ];

  // 导出数据映射函数
  const exportDataMapper = (item: Supplier, t: any) => {
    return {
      [t('purchase.code')]: item?.code || '',
      [t('purchase.principal')]: item?.principal || '',
      [t('purchase.name')]: item?.name || '',
      [t('purchase.shortName')]: item?.shortName || '',
      [t('purchase.country')]: item?.country || '',
      [t('purchase.address')]: item?.address || '',
      [t('purchase.zipCode')]: item?.zipCode || '',
      [t('purchase.email')]: item?.email || '',
      [t('purchase.contact')]: item?.contact || '',
      [t('purchase.phone')]: item?.phone || '',
      [t('purchase.fax')]: item?.fax || '',
      [t('purchase.mobile')]: item?.mobile || '',
      [t('purchase.account1')]: item?.account1 || '',
      [t('purchase.bank1')]: item?.bank1 || '',
      [t('purchase.swiftCode1')]: item?.swiftCode1 || '',
      [t('purchase.account2')]: item?.account2 || '',
      [t('purchase.bank2')]: item?.bank2 || '',
      [t('purchase.swiftCode2')]: item?.swiftCode2 || '',
      [t('purchase.currency')]: currencys.find(cur => cur.key === item.currency)?.desc || '',
      [t('purchase.creditLevel')]: item?.creditLevel || '',
      [t('purchase.creditLine')]: item?.creditLine || '',
      [t('purchase.depositRate')]: item?.depositRate || '',
      [t('purchase.tradeTerm')]: purchaseTradeTerm.find(ter => ter.id === item.tradeTerm)?.desc || '',
      [t('purchase.payMethod')]: purchasePayMethod.find(pay => pay.id === item.payMethod)?.key || '',
      [t('purchase.affiliates')]: item?.affiliates || '',
      [t('purchase.org')]: orgs.find(o => o.id === item.orgId)?.name ?? '',
      [t('purchase.billingDays')]: item?.billingDays || '',
      [t('purchase.taxRate')]: item?.taxRate || '',
      [t('purchase.thdCode')]: item?.thdCode || '',
    };
  };

  const headerChildren = (
    <Flex style={{ width: "100%" }} justify="space-between">
      <Flex gap={10}>
        {appliedState !== CommonStateEnum.UN_ACTIVE && <Button
          onClick={() => setAddOpen(true)}
          type="primary"
          style={{ marginBottom: 16 }}
        >
          {t("purchase.addCustomer")}
        </Button>}
        <ExportExcelButton<Supplier>
          list={list}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName="suppliers.xlsx"
          sheetName={t('purchase.supplier')}
        />
        <ExportExcelButton<Supplier>
          list={list}
          isAll
          loadData={handleExportAll}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName="suppliers_all.xlsx"
          sheetName={t('purchase.supplier')}
        />
        <Button onClick={() => onExportPrice(undefined, true)}>{t('purchase.allPriceHis')}</Button>
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <>
              <Select
                style={{ width: 100 }}
                value={state}
                onChange={value => updateDraftFilters({ state: value })}
                options={[
                  { label: t("org.enable"), value: CommonStateEnum.ACTIVE },
                  { label: t("org.disable"), value: CommonStateEnum.UN_ACTIVE },
                ]}
              />
              <Tooltip title={t("purchase.bySearchSupplier")}>
                <Input
                  placeholder={t("purchase.bySearchSupplier")}
                  style={{ width: 200 }}
                  value={keyword}
                  onChange={event =>
                    updateDraftFilters({ keyword: event.target.value.trim() })
                  }
                  onPressEnter={submit}
                />
              </Tooltip>
            </>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  )

  return (
    <Flex vertical style={{ width: '100%', height: '100%' }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={3800}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
      />
      <SupplierForm
        key={"add"}
        open={addOpen}
        confirmLoading={addLoading}
        handleCancel={() => setAddOpen(false)}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            add({ ...data, code: data.code.trim() })
              .then((res) => {
                addItem(res);
                setAddOpen(false);
                notification.success({ placement: 'top', message: t('common.addSuccess') })
                resolve();
              })
              .catch((err) => {
                notification.error({
                  placement: 'top',
                  message: t("common.addError"),
                  description: errors[err] ?? t("common.serverError"),
                });
              });
          });
        }}
        leftTitle={t("common.add")}
        data={null}
      />
      <SupplierForm
        key={detail?.id}
        open={!!detail}
        confirmLoading={updateLoading}
        handleCancel={() => setDetail(null)}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            if (detail) {
              update(data, detail.id)
                .then(() => {
                  updateItem({ ...detail, ...data });
                  setDetail(null);
                  notification.success({ placement: 'top', message: t('common.saveOk') })
                  resolve();
                })
                .catch((err) => {
                  notification.error({
                    placement: 'top',
                    message: t("common.editError"),
                    description: errors[err] ?? t("common.serverError"),
                  });
                  reject();
                });
            }
          });
        }}
        leftTitle={t("common.edit")}
        data={detail}
      />
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
      <ExportProgressModal visible={exportProgressVisible} progress={exportPriceProgress} />
    </Flex>
  );
};

export default CustomerTable;
